import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Grid,
  Card,
  CardContent,
  Typography,
  Box,
  LinearProgress,
  Chip,
  IconButton,
  Tooltip,
  Avatar,
  Fab,
  Button,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Divider
} from '@mui/material';
import {
  People,
  MedicalServices,
  TrendingUp,
  Warning,
  CheckCircle,
  Refresh,
  Assessment,
  LocalHospital,
  EventNote,
  Schedule,
  MonitorHeart,
  Medication,
  History,
  PersonAdd,
  Add,
  Download,
  Archive,
  TableChart,
  PictureAsPdf
} from '@mui/icons-material';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, ResponsiveContainer } from 'recharts';
import { useDataContext } from '../contexts/DataContext';
import { useFeedback } from './EnhancedFeedbackProvider';
import StatCard from './StatCard';
import { generatePatientReport } from '../utils/pdfGenerator';
import { generatePatientExcelReport } from '../utils/excelExport';
import { createBackup } from '../services/backupService';
import JSZip from 'jszip';

const MedicalDashboard = () => {
  const [stats, setStats] = useState(null);
  const [trends, setTrends] = useState([]);
  const [loading, setLoading] = useState(true);
  const [exportMenuAnchor, setExportMenuAnchor] = useState(null);
  const [exporting, setExporting] = useState(false);
  const { showError, showLoading, showSuccess } = useFeedback();
  const { patients, consultations, vitals, treatments, medicalHistory, observations } = useDataContext();
  const navigate = useNavigate();

  const calculateDashboardData = () => {
    try {
      showLoading(true);
      
      // Calculate stats from local data
      const today = new Date().toISOString().split('T')[0];
      const weekAgo = new Date();
      weekAgo.setDate(weekAgo.getDate() - 7);
      
      const calculatedStats = {
        totalPatients: patients.length,
        totalConsultations: consultations.length,
        totalVitals: vitals.length,
        totalTreatments: treatments.length,
        totalMedicalHistory: medicalHistory.length,
        totalObservations: observations ? observations.length : 0,
        
        // Consultations aujourd'hui
        consultationsToday: consultations.filter(c => {
          if (!c.consultation_date) return false;
          return c.consultation_date.split('T')[0] === today;
        }).length,
        
        // Consultations récentes (7 derniers jours)
        recentConsultations: consultations.filter(c => {
          if (!c.consultation_date) return false;
          const consultationDate = new Date(c.consultation_date);
          return consultationDate >= weekAgo;
        }).length,
        
        // Rendez-vous à venir (7 prochains jours)
        upcomingAppointments: consultations.filter(c => {
          if (!c.next_appointment) return false;
          const appointmentDate = new Date(c.next_appointment);
          const today = new Date();
          const nextWeek = new Date();
          nextWeek.setDate(today.getDate() + 7);
          return appointmentDate >= today && appointmentDate <= nextWeek;
        }).length,
        
        // Traitements actifs
        activeTreatments: treatments.filter(t => !t.end_date).length,
        completedTreatments: treatments.filter(t => t.end_date).length,
        
        // Constantes vitales enregistrées aujourd'hui
        vitalsRecordedToday: vitals.filter(v => {
          if (!v.measurement_date) return false;
          return v.measurement_date.split('T')[0] === today;
        }).length,
        
        // Antécédents ajoutés cette semaine
        medicalHistoryAdded: medicalHistory.filter(h => {
          if (!h.createdAt) return false;
          const historyDate = new Date(h.createdAt);
          return historyDate >= weekAgo;
        }).length,
        
        // Patients urgents (avec consultations récentes)
        urgentPatients: patients.filter(p => {
          const patientConsultations = consultations.filter(c => c.patientId === p.id);
          return patientConsultations.some(c => {
            if (!c.consultation_date) return false;
            const consultationDate = new Date(c.consultation_date);
            return consultationDate >= weekAgo;
          });
        }).length,
        
      };
      
      // Calculate trends (last 7 days)
      const trendsData = [];
      for (let i = 6; i >= 0; i--) {
        const date = new Date();
        date.setDate(date.getDate() - i);
        const dateStr = date.toISOString().split('T')[0];
        
        const dayConsultations = consultations.filter(c => {
          if (!c.consultation_date) return false;
          return c.consultation_date.split('T')[0] === dateStr;
        }).length;
        
        const dayVitals = vitals.filter(v => {
          if (!v.measurement_date) return false;
          return v.measurement_date.split('T')[0] === dateStr;
        }).length;
        
        const dayPatients = patients.filter(p => {
          if (!p.createdAt) return false;
          return p.createdAt.split('T')[0] === dateStr;
        }).length;
        
        trendsData.push({
          date: dateStr,
          consultations: dayConsultations,
          vitals: dayVitals,
          patients: dayPatients
        });
      }
      
      setStats(calculatedStats);
      setTrends(trendsData);
    } catch (error) {
      console.error('Erreur lors du calcul du dashboard:', error);
      showError('Erreur lors du calcul des données du tableau de bord');
    } finally {
      setLoading(false);
      showLoading(false);
    }
  };

  // Fonctions d'export
  const handleExportMenuOpen = (event) => {
    setExportMenuAnchor(event.currentTarget);
  };

  const handleExportMenuClose = () => {
    setExportMenuAnchor(null);
  };


  const calculateAge = (dateOfBirth) => {
    const today = new Date();
    const birthDate = new Date(dateOfBirth);
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    
    return age;
  };

  const formatDate = (date) => {
    if (!date) return 'Jamais';
    return new Date(date).toLocaleDateString('fr-FR');
  };

  const handleExportAllPatients = async () => {
    try {
      setExporting(true);
      showLoading(true, 'Export de la liste des patients...');

      // Créer le contenu CSV
      const csvContent = [
        // En-têtes
        ['Nom', 'Prénom', 'Date de naissance', 'Âge', 'Sexe', 'Email', 'Téléphone', 'Adresse', 'Contact d\'urgence', 'Téléphone d\'urgence', 'Numéro de dossier', 'Médecin', 'Date de création'].join(','),
        // Données
        ...patients.map(patient => [
          `"${patient.lastName || ''}"`,
          `"${patient.firstName || ''}"`,
          `"${formatDate(patient.dateOfBirth)}"`,
          `"${calculateAge(patient.dateOfBirth)}"`,
          `"${patient.gender || ''}"`,
          `"${patient.email || ''}"`,
          `"${patient.phone || ''}"`,
          `"${patient.address || ''}"`,
          `"${patient.emergencyContact || ''}"`,
          `"${patient.emergencyPhone || ''}"`,
          `"${patient.medicalRecordNumber || ''}"`,
          `"${patient.createdBy || 'Non spécifié'}"`,
          `"${formatDate(patient.createdAt)}"`
        ].join(','))
      ].join('\n');

      // Créer et télécharger le fichier
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `liste_patients_${new Date().toISOString().split('T')[0]}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      showSuccess('Liste des patients exportée avec succès');
    } catch (error) {
      console.error('Erreur lors de l\'export:', error);
      showError('Erreur lors de l\'export de la liste des patients');
    } finally {
      setExporting(false);
      showLoading(false);
      handleExportMenuClose();
    }
  };

  // Fonction pour générer un PDF en mémoire
  const generatePatientReportInMemory = async (patient, consultations, vitals, treatments, medicalHistory) => {
    const { jsPDF } = await import('jspdf');
    const autoTable = (await import('jspdf-autotable')).default;
    
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    
    // En-tête
    doc.setFontSize(20);
    doc.setFont('helvetica', 'bold');
    doc.text('DOSSIER MÉDICAL', pageWidth / 2, 20, { align: 'center' });
    
    doc.setFontSize(14);
    doc.setFont('helvetica', 'normal');
    doc.text(`${patient.firstName} ${patient.lastName}`, pageWidth / 2, 30, { align: 'center' });
    
    let yPosition = 50;
    
    // Informations patient
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.text('INFORMATIONS PATIENT', 20, yPosition);
    yPosition += 10;
    
    doc.setFont('helvetica', 'normal');
    const patientInfo = [
      ['Nom', patient.lastName || ''],
      ['Prénom', patient.firstName || ''],
      ['Date de naissance', patient.dateOfBirth ? new Date(patient.dateOfBirth).toLocaleDateString('fr-FR') : ''],
      ['Sexe', patient.gender === 'M' ? 'Masculin' : patient.gender === 'F' ? 'Féminin' : ''],
      ['Téléphone', patient.phone || ''],
      ['Email', patient.email || ''],
      ['Adresse', patient.address || ''],
      ['Contact d\'urgence', patient.emergencyContact || ''],
      ['Téléphone d\'urgence', patient.emergencyPhone || ''],
      ['Numéro de dossier', patient.medicalRecordNumber || ''],
      ['Créé par', patient.createdBy || ''],
      ['Date de création', patient.createdAt ? new Date(patient.createdAt).toLocaleDateString('fr-FR') : '']
    ];
    
    autoTable(doc, {
      head: [['Champ', 'Valeur']],
      body: patientInfo,
      startY: yPosition,
      styles: { fontSize: 10 },
      headStyles: { fillColor: [66, 139, 202] }
    });
    
    yPosition = doc.lastAutoTable.finalY + 20;
    
    // Consultations
    if (consultations.length > 0) {
      doc.setFont('helvetica', 'bold');
      doc.text('CONSULTATIONS', 20, yPosition);
      yPosition += 10;
      
      const consultationsData = consultations.map(c => [
        c.consultation_date ? new Date(c.consultation_date).toLocaleDateString('fr-FR') : '',
        c.reason || '',
        c.diagnosis || '',
        c.notes || ''
      ]);
      
      autoTable(doc, {
        head: [['Date', 'Motif', 'Diagnostic', 'Notes']],
        body: consultationsData,
        startY: yPosition,
        styles: { fontSize: 9 },
        headStyles: { fillColor: [66, 139, 202] }
      });
      
      yPosition = doc.lastAutoTable.finalY + 20;
    }
    
    // Constantes vitales
    if (vitals.length > 0) {
      doc.setFont('helvetica', 'bold');
      doc.text('CONSTANTES VITALES', 20, yPosition);
      yPosition += 10;
      
      const vitalsData = vitals.map(v => [
        v.measurement_date ? new Date(v.measurement_date).toLocaleDateString('fr-FR') : '',
        v.systolic_pressure || '',
        v.diastolic_pressure || '',
        v.heart_rate || '',
        v.temperature || '',
        v.weight || '',
        v.height || ''
      ]);
      
      autoTable(doc, {
        head: [['Date', 'Tension Syst.', 'Tension Diast.', 'Pouls', 'Température', 'Poids', 'Taille']],
        body: vitalsData,
        startY: yPosition,
        styles: { fontSize: 9 },
        headStyles: { fillColor: [66, 139, 202] }
      });
      
      yPosition = doc.lastAutoTable.finalY + 20;
    }
    
    // Traitements
    if (treatments.length > 0) {
      doc.setFont('helvetica', 'bold');
      doc.text('TRAITEMENTS', 20, yPosition);
      yPosition += 10;
      
      const treatmentsData = treatments.map(t => [
        t.medication_name || '',
        t.dosage || '',
        t.frequency || '',
        t.start_date ? new Date(t.start_date).toLocaleDateString('fr-FR') : '',
        t.end_date ? new Date(t.end_date).toLocaleDateString('fr-FR') : '',
        t.notes || ''
      ]);
      
      autoTable(doc, {
        head: [['Médicament', 'Dosage', 'Fréquence', 'Début', 'Fin', 'Notes']],
        body: treatmentsData,
        startY: yPosition,
        styles: { fontSize: 9 },
        headStyles: { fillColor: [66, 139, 202] }
      });
      
      yPosition = doc.lastAutoTable.finalY + 20;
    }
    
    // Antécédents médicaux
    if (medicalHistory.length > 0) {
      doc.setFont('helvetica', 'bold');
      doc.text('ANTÉCÉDENTS MÉDICAUX', 20, yPosition);
      yPosition += 10;
      
      const historyData = medicalHistory.map(h => [
        h.history_type || '',
        h.title || '',
        h.description || '',
        h.start_date ? new Date(h.start_date).toLocaleDateString('fr-FR') : '',
        h.end_date ? new Date(h.end_date).toLocaleDateString('fr-FR') : ''
      ]);
      
      autoTable(doc, {
        head: [['Type', 'Titre', 'Description', 'Début', 'Fin']],
        body: historyData,
        startY: yPosition,
        styles: { fontSize: 9 },
        headStyles: { fillColor: [66, 139, 202] }
      });
    }
    
    // Pied de page
    const pageCount = doc.internal.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      doc.setFontSize(8);
      doc.text(`Page ${i} sur ${pageCount}`, pageWidth - 30, pageHeight - 10);
    }
    
    return doc.output('blob');
  };

  // Fonction pour générer un Excel en mémoire
  const generatePatientExcelInMemory = async (patient, consultations, vitals, treatments, medicalHistory) => {
    const XLSX = await import('xlsx');
    
    const workbook = XLSX.utils.book_new();
    
    // Feuille Patient
    const patientData = [
      ['Nom', patient.lastName || ''],
      ['Prénom', patient.firstName || ''],
      ['Date de naissance', patient.dateOfBirth ? new Date(patient.dateOfBirth).toLocaleDateString('fr-FR') : ''],
      ['Sexe', patient.gender === 'M' ? 'Masculin' : patient.gender === 'F' ? 'Féminin' : ''],
      ['Téléphone', patient.phone || ''],
      ['Email', patient.email || ''],
      ['Adresse', patient.address || ''],
      ['Contact d\'urgence', patient.emergencyContact || ''],
      ['Téléphone d\'urgence', patient.emergencyPhone || ''],
      ['Numéro de dossier', patient.medicalRecordNumber || ''],
      ['Créé par', patient.createdBy || ''],
      ['Date de création', patient.createdAt ? new Date(patient.createdAt).toLocaleDateString('fr-FR') : '']
    ];
    
    const patientSheet = XLSX.utils.aoa_to_sheet(patientData);
    XLSX.utils.book_append_sheet(workbook, patientSheet, 'Patient');
    
    // Feuille Consultations
    if (consultations.length > 0) {
      const consultationsData = [
        ['Date', 'Motif', 'Diagnostic', 'Notes', 'Prochain RDV'],
        ...consultations.map(c => [
          c.consultation_date ? new Date(c.consultation_date).toLocaleDateString('fr-FR') : '',
          c.reason || '',
          c.diagnosis || '',
          c.notes || '',
          c.next_appointment ? new Date(c.next_appointment).toLocaleDateString('fr-FR') : ''
        ])
      ];
      const consultationsSheet = XLSX.utils.aoa_to_sheet(consultationsData);
      XLSX.utils.book_append_sheet(workbook, consultationsSheet, 'Consultations');
    }
    
    // Feuille Constantes Vitales
    if (vitals.length > 0) {
      const vitalsData = [
        ['Date', 'Tension Systolique', 'Tension Diastolique', 'Pouls', 'Température', 'Poids', 'Taille'],
        ...vitals.map(v => [
          v.measurement_date ? new Date(v.measurement_date).toLocaleDateString('fr-FR') : '',
          v.systolic_pressure || '',
          v.diastolic_pressure || '',
          v.heart_rate || '',
          v.temperature || '',
          v.weight || '',
          v.height || ''
        ])
      ];
      const vitalsSheet = XLSX.utils.aoa_to_sheet(vitalsData);
      XLSX.utils.book_append_sheet(workbook, vitalsSheet, 'Constantes Vitales');
    }
    
    // Feuille Traitements
    if (treatments.length > 0) {
      const treatmentsData = [
        ['Médicament', 'Dosage', 'Fréquence', 'Date de début', 'Date de fin', 'Notes'],
        ...treatments.map(t => [
          t.medication_name || '',
          t.dosage || '',
          t.frequency || '',
          t.start_date ? new Date(t.start_date).toLocaleDateString('fr-FR') : '',
          t.end_date ? new Date(t.end_date).toLocaleDateString('fr-FR') : '',
          t.notes || ''
        ])
      ];
      const treatmentsSheet = XLSX.utils.aoa_to_sheet(treatmentsData);
      XLSX.utils.book_append_sheet(workbook, treatmentsSheet, 'Traitements');
    }
    
    // Feuille Antécédents
    if (medicalHistory.length > 0) {
      const historyData = [
        ['Type', 'Titre', 'Description', 'Date de début', 'Date de fin'],
        ...medicalHistory.map(h => [
          h.history_type || '',
          h.title || '',
          h.description || '',
          h.start_date ? new Date(h.start_date).toLocaleDateString('fr-FR') : '',
          h.end_date ? new Date(h.end_date).toLocaleDateString('fr-FR') : ''
        ])
      ];
      const historySheet = XLSX.utils.aoa_to_sheet(historyData);
      XLSX.utils.book_append_sheet(workbook, historySheet, 'Antécédents');
    }
    
    return XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
  };

  // Fonction pour l'export individuel (comme avant)
  const handleExportAllPatientFilesIndividual = async (format) => {
    try {
      setExporting(true);
      showLoading(true, `Export de tous les dossiers patients en ${format.toUpperCase()}...`);

      let exportedCount = 0;
      const totalPatients = patients.length;

      for (const patient of patients) {
        try {
          // Récupérer les données du patient
          const patientConsultations = consultations.filter(c => c.patientId === patient.id);
          const patientVitals = vitals.filter(v => v.patientId === patient.id);
          const patientTreatments = treatments.filter(t => t.patientId === patient.id);
          const patientMedicalHistory = medicalHistory.filter(h => h.patientId === patient.id);

          // Générer l'export selon le format
          if (format === 'pdf') {
            generatePatientReport(
              patient,
              patientConsultations,
              patientVitals,
              patientTreatments,
              patientMedicalHistory
            );
          } else if (format === 'excel') {
            generatePatientExcelReport(
              patient,
              patientConsultations,
              patientVitals,
              patientTreatments,
              patientMedicalHistory
            );
          }

          exportedCount++;
          
          // Mettre à jour le message de progression
          showLoading(true, `Export en cours... ${exportedCount}/${totalPatients} patients`);
          
          // Petite pause pour éviter de surcharger le navigateur
          await new Promise(resolve => setTimeout(resolve, 100));
        } catch (patientError) {
          console.error(`Erreur lors de l'export du patient ${patient.firstName} ${patient.lastName}:`, patientError);
        }
      }

      showSuccess(`${exportedCount} dossiers patients exportés avec succès en ${format.toUpperCase()}`);
    } catch (error) {
      console.error('Erreur lors de l\'export:', error);
      showError('Erreur lors de l\'export des dossiers patients');
    } finally {
      setExporting(false);
      showLoading(false);
      handleExportMenuClose();
    }
  };

  // Fonction pour l'export en archive ZIP
  const handleExportAllPatientFilesZip = async (format) => {
    try {
      setExporting(true);
      showLoading(true, `Création de l'archive ZIP avec tous les dossiers patients...`);

      const zip = new JSZip();
      const today = new Date().toISOString().split('T')[0];
      
      // Créer le dossier principal
      const mainFolder = zip.folder(`dossiers_patients_${today}`);
      
      let exportedCount = 0;
      const totalPatients = patients.length;

      for (const patient of patients) {
        try {
          // Récupérer les données du patient
          const patientConsultations = consultations.filter(c => c.patientId === patient.id);
          const patientVitals = vitals.filter(v => v.patientId === patient.id);
          const patientTreatments = treatments.filter(t => t.patientId === patient.id);
          const patientMedicalHistory = medicalHistory.filter(h => h.patientId === patient.id);

          // Créer un dossier pour ce patient
          const patientFolder = mainFolder.folder(`${patient.lastName}_${patient.firstName}`);
          
          // Générer l'export selon le format
          if (format === 'pdf') {
            const pdfBlob = await generatePatientReportInMemory(
              patient,
              patientConsultations,
              patientVitals,
              patientTreatments,
              patientMedicalHistory
            );
            patientFolder.file(`dossier_${patient.lastName}_${patient.firstName}.pdf`, pdfBlob);
          } else if (format === 'excel') {
            const excelData = await generatePatientExcelInMemory(
              patient,
              patientConsultations,
              patientVitals,
              patientTreatments,
              patientMedicalHistory
            );
            patientFolder.file(`dossier_${patient.lastName}_${patient.firstName}.xlsx`, excelData);
          }

          exportedCount++;
          
          // Mettre à jour le message de progression
          showLoading(true, `Création de l'archive... ${exportedCount}/${totalPatients} patients`);
          
          // Petite pause pour éviter de surcharger le navigateur
          await new Promise(resolve => setTimeout(resolve, 50));
        } catch (patientError) {
          console.error(`Erreur lors de l'export du patient ${patient.firstName} ${patient.lastName}:`, patientError);
        }
      }

      // Ajouter la liste des patients dans le dossier principal
      const csvContent = [
        // En-têtes
        ['Nom', 'Prénom', 'Date de naissance', 'Âge', 'Sexe', 'Email', 'Téléphone', 'Adresse', 'Contact d\'urgence', 'Téléphone d\'urgence', 'Numéro de dossier', 'Médecin', 'Date de création'].join(','),
        // Données
        ...patients.map(patient => [
          `"${patient.lastName || ''}"`,
          `"${patient.firstName || ''}"`,
          `"${formatDate(patient.dateOfBirth)}"`,
          `"${calculateAge(patient.dateOfBirth)}"`,
          `"${patient.gender || ''}"`,
          `"${patient.email || ''}"`,
          `"${patient.phone || ''}"`,
          `"${patient.address || ''}"`,
          `"${patient.emergencyContact || ''}"`,
          `"${patient.emergencyPhone || ''}"`,
          `"${patient.medicalRecordNumber || ''}"`,
          `"${patient.createdBy || 'Non spécifié'}"`,
          `"${formatDate(patient.createdAt)}"`
        ].join(','))
      ].join('\n');
      
      mainFolder.file('liste_patients.csv', csvContent);

      // Générer et télécharger le ZIP
      showLoading(true, 'Finalisation de l\'archive ZIP...');
      const zipBlob = await zip.generateAsync({ type: 'blob' });
      
      // Télécharger le fichier ZIP
      const link = document.createElement('a');
      const url = URL.createObjectURL(zipBlob);
      link.setAttribute('href', url);
      link.setAttribute('download', `dossiers_patients_${today}.zip`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      showSuccess(`Archive ZIP créée avec succès ! ${exportedCount} dossiers patients exportés en ${format.toUpperCase()}`);
    } catch (error) {
      console.error('Erreur lors de l\'export:', error);
      showError('Erreur lors de la création de l\'archive ZIP');
    } finally {
      setExporting(false);
      showLoading(false);
      handleExportMenuClose();
    }
  };

  useEffect(() => {
    calculateDashboardData();
  }, [patients, consultations, vitals, treatments, medicalHistory, observations]);

  if (loading) {
    return (
      <Box sx={{ width: '100%', mt: 2 }}>
        <LinearProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* En-tête avec actions */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" component="h1" fontWeight="bold">
          Tableau de Bord Médical
        </Typography>
        <Box display="flex" gap={1}>
          <Button
            variant="outlined"
            startIcon={<Download />}
            onClick={handleExportMenuOpen}
            disabled={exporting}
            sx={{ borderRadius: 2 }}
          >
            {exporting ? 'Export...' : 'Exporter Tout'}
          </Button>
          <Tooltip title="Actualiser les données">
            <IconButton onClick={calculateDashboardData} color="primary">
              <Refresh />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* Cartes de statistiques */}
      <Grid container spacing={3} mb={4}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Total Patients"
            value={stats?.totalPatients || 0}
            icon={<People />}
            color="primary"
            trend={stats?.newPatientsThisMonth ? `+${stats.newPatientsThisMonth}` : '+0'}
            subtitle="Ce mois"
            loading={loading}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Consultations Aujourd'hui"
            value={stats?.consultationsToday || 0}
            icon={<EventNote />}
            color="secondary"
            trend={stats?.consultationsThisWeek ? `+${stats.consultationsThisWeek}` : '+0'}
            subtitle="Cette semaine"
            loading={loading}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Rendez-vous à venir"
            value={stats?.upcomingAppointments || 0}
            icon={<Schedule />}
            color="warning"
            subtitle="Prochaines 48h"
            loading={loading}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Traitements Actifs"
            value={stats?.activeTreatments || 0}
            icon={<Medication />}
            color="info"
            subtitle="En cours"
            loading={loading}
          />
        </Grid>
      </Grid>

      {/* Deuxième ligne de métriques */}
      <Grid container spacing={3} mb={4}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Patients Urgents"
            value={stats?.urgentPatients || 0}
            icon={<Warning />}
            color="error"
            subtitle="Attention requise"
            loading={loading}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Constantes Vitales"
            value={stats?.vitalsRecordedToday || 0}
            icon={<MonitorHeart />}
            color="success"
            subtitle="Enregistrées aujourd'hui"
            loading={loading}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Antécédents Ajoutés"
            value={stats?.medicalHistoryAdded || 0}
            icon={<History />}
            color="info"
            subtitle="Ce mois"
            loading={loading}
          />
        </Grid>
      </Grid>

      {/* Graphiques utiles */}
      <Grid container spacing={3}>
        <Grid item xs={12} md={8}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Activité des 7 derniers jours
              </Typography>
              <Box sx={{ height: 300, mt: 2 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={trends}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis 
                      dataKey="date" 
                      tickFormatter={(value) => new Date(value).toLocaleDateString('fr-FR', { weekday: 'short' })}
                    />
                    <YAxis />
                    <RechartsTooltip 
                      labelFormatter={(value) => new Date(value).toLocaleDateString('fr-FR')}
                    />
                    <Line 
                      type="monotone" 
                      dataKey="consultations" 
                      stroke="#1976d2" 
                      strokeWidth={2}
                      name="Consultations"
                    />
                    <Line 
                      type="monotone" 
                      dataKey="newPatients" 
                      stroke="#2e7d32" 
                      strokeWidth={2}
                      name="Nouveaux Patients"
                    />
                    <Line 
                      type="monotone" 
                      dataKey="vitals" 
                      stroke="#ed6c02" 
                      strokeWidth={2}
                      name="Constantes Vitales"
                    />
                  </LineChart>
                </ResponsiveContainer>
              </Box>
            </CardContent>
          </Card>
        </Grid>

      </Grid>
      
      {/* FAB pour ajouter un nouveau patient */}
      <Fab
        color="primary"
        aria-label="add patient"
        sx={{ 
          position: 'fixed', 
          bottom: 24, 
          right: 24,
          boxShadow: 3,
        }}
        onClick={() => {
          // Rediriger vers la page de création de nouveau patient
          navigate('/patients/new');
        }}
      >
        <Add />
      </Fab>

      {/* Menu d'export */}
      <Menu
        anchorEl={exportMenuAnchor}
        open={Boolean(exportMenuAnchor)}
        onClose={handleExportMenuClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        <MenuItem onClick={handleExportAllPatients}>
          <ListItemIcon>
            <TableChart fontSize="small" />
          </ListItemIcon>
          <ListItemText>Liste des patients (CSV)</ListItemText>
        </MenuItem>
        
        <Divider />
        
        <MenuItem onClick={() => handleExportAllPatientFilesIndividual('excel')}>
          <ListItemIcon>
            <TableChart fontSize="small" />
          </ListItemIcon>
          <ListItemText>Tous les dossiers (Excel)</ListItemText>
        </MenuItem>
        
        <MenuItem onClick={() => handleExportAllPatientFilesIndividual('pdf')}>
          <ListItemIcon>
            <PictureAsPdf fontSize="small" />
          </ListItemIcon>
          <ListItemText>Tous les dossiers (PDF)</ListItemText>
        </MenuItem>
        
        <Divider />
        
        <MenuItem onClick={() => handleExportAllPatientFilesZip('excel')}>
          <ListItemIcon>
            <Archive fontSize="small" />
          </ListItemIcon>
          <ListItemText>Archive ZIP - Dossiers Excel</ListItemText>
        </MenuItem>
        
        <MenuItem onClick={() => handleExportAllPatientFilesZip('pdf')}>
          <ListItemIcon>
            <Archive fontSize="small" />
          </ListItemIcon>
          <ListItemText>Archive ZIP - Dossiers PDF</ListItemText>
        </MenuItem>
      </Menu>
    </Box>
  );
};

export default MedicalDashboard;
